import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  gql,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';

// The only export of `gql` on the client: every query module receives it through loadGQL().
export { gql } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';

import { appSettings } from '@/settings/app-settings';

import { updateAppSession } from '../apis/app-api';
import {
  getClientSessionData,
  getCookieStorageFromServer,
  getGuestUserJwt,
  hostName,
  isCustomDomain,
  updateServerCookie,
} from '../utils/auth-utils';
import { isServer } from '../utils/helpers';
import { isExpiredJwtGraphQLError } from '../utils/is-expired-jwt-error';
import { serverLogger } from '../utils/logger';
import { getTokenRefreshManager } from '../utils/token-refresh-manager';

export const runtime = 'nodejs';

let gqlPath: string | undefined = '';
let gqlClient: ApolloClient<NormalizedCacheObject>;
let authTool: {
  sendAuthExpired: () => void;
  refreshContextData: (fresh?: boolean) => Promise<void> | void;
  clearCartData: () => void;
} = {
  sendAuthExpired: () => {},
  refreshContextData: async (_fresh?: boolean) => {},
  clearCartData: () => {},
};

// SECURITY FIX: User-scoped caches to prevent cross-user data leakage
// These caches are keyed by user-specific identifiers to ensure isolation

const refreshTokenPromises = new Map<string, Promise<FetchResult<Record<string, any>>>>();

// SECURITY: Generate unique cache key for refresh token promises
// CRITICAL: Never share promises when userId is missing to prevent cross-user token leakage
const getRefreshPromiseKey = (tenantId: string, refreshToken: string, userId?: string) => {
  if (!userId) {
    // SECURITY: When userId is unavailable (e.g., transient cookie read failure),
    // use the FULL refresh token to ensure uniqueness. Never truncate or share promises
    // without a userId, as this can cause different users to receive each other's tokens.
    return `${tenantId}:${refreshToken}`;
  }

  // When userId is available, we can safely use a shorter hash since userId provides isolation
  const tokenHash = refreshToken.substring(0, 20);
  return `${tenantId}:${userId}:${tokenHash}`;
};

type TenantInfoCacheEntry = {
  promise: Promise<FetchResult<Record<string, any>>>;
  timestamp: number;
  userId?: string; // Track which user this cache belongs to
};
const tenantInfoCache = new Map<string, TenantInfoCacheEntry>();
const TENANT_INFO_CACHE_DURATION = 60000; // 1 minute
const tenantInfoSSRInflight = new Map<string, Promise<FetchResult<Record<string, any>>>>();
const shouldLogGraphql =
  process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_DEBUG_GRAPHQL === 'true';
const gqlDebug = (...args: any[]) => {
  if (shouldLogGraphql) {
    console.log(...args);
  }
};

export async function refreshAndRetry(
  operation: any,
  forward: any,
): Promise<FetchResult<Record<string, any>>> {
  // Minimal logging for production
  gqlDebug('refreshAndRetry: Operation:', operation.operationName);
  // Detailed logging commented out for production
  // console.log('=== REFRESH AND RETRY TRIGGERED ===');
  // console.log('Timestamp:', new Date().toISOString());
  // console.log('Operation Name:', operation.operationName);
  // console.log('Operation Variables:', operation.variables);
  // console.log('===================================');

  // SECURITY FIX: Get user-scoped token refresh manager
  // This prevents one user's refresh failures from blocking another user
  const cookiesData = isServer() ? await getCookieStorageFromServer() : await getClientSessionData();
  const manager = getTokenRefreshManager(cookiesData?.userId, cookiesData?.tenantId);

  const refreshOutcome = await manager.executeRefreshDetailed(async () => {
    // console.log('=== TOKEN REFRESH CONTEXT ===');
    // console.log('Is Server:', isServer());
    // console.log('Has Refresh Token:', !!cookiesData?.refreshToken);
    // console.log('Tenant ID:', cookiesData?.tenantId);
    // console.log('User Role:', cookiesData?.role);
    // console.log('Current JWT exists:', !!cookiesData?.jwt);
    // console.log('=============================');

    const isAuthenticatedUser =
      cookiesData?.refreshToken &&
      cookiesData.refreshToken !== 'null' &&
      cookiesData.refreshToken !== 'undefined' &&
      cookiesData?.role !== 'guest';

    if (isAuthenticatedUser && cookiesData?.tenantId) {
      let accessToken: any;
      try {
        gqlDebug('Calling getNewJwt for authenticated user');
        // Pass the expired JWT from cookies to ensure it's used in the authorization header
        accessToken = await getNewJwt(cookiesData.tenantId, cookiesData.refreshToken, cookiesData.jwt);
      } catch (refreshError) {
        gqlDebug('graphql log: errorLink: Refresh token failed - logging out', refreshError);
        // console.error('=== REFRESH TOKEN FAILED ===');
        // console.error('Error:', refreshError);
        // console.error('============================');
        return false;
      }

      if (accessToken?.data?.getJwtTokenV2) {
        if (isServer()) {
          await updateServerCookie(
            accessToken.data.getJwtTokenV2?.jwt,
            accessToken.data.getJwtTokenV2?.refreshToken,
          );
        } else {
          await updateAppSession({
            jwt: accessToken.data.getJwtTokenV2?.jwt,
            refreshToken: accessToken.data.getJwtTokenV2.refreshToken,
          });
        }
        return true;
      }
      return false;
    } else if (!isAuthenticatedUser && cookiesData?.role === 'guest') {
      gqlDebug('graphql log: errorLink: Getting new guest token');
      if (!isServer()) {
        // For custom domains on client side, use the current host
        const currentHost = window.location.host;

        if (isCustomDomain(currentHost)) {
          gqlDebug('graphql log: errorLink: Custom domain detected for guest token refresh:', currentHost);
          // Use the tenant JWT endpoint which handles custom domains properly
          try {
            const response = await fetch('/api/tenant/jwt');
            if (response.ok) {
              const data = await response.json();
              if (data?.jwt) {
                await updateAppSession({ jwt: data.jwt, refreshToken: null });
                return true;
              }
            }
          } catch (error) {
            gqlDebug('graphql log: errorLink: Failed to get guest token via API, falling back', error);
          }
        }
        // Fallback to regular guest token
        await getGuestUserJwt();
      } else {
        const host = hostName || appSettings.api.CLIENT_APP_URL;
        const guestData = await getTenantInfoJwtSSR(host || '');
        if (guestData?.data?.getTenantInfo?.jwt) {
          await updateServerCookie(
            guestData.data.getTenantInfo.jwt,
            guestData.data.getTenantInfo.refreshToken,
          );
        }
      }
      return true;
    }

    return false;
  });

  if (refreshOutcome === 'retry-later') {
    // Backing off after earlier failures, too soon since the last attempt, or the refresh call
    // itself never got an answer. None of those mean the session is over — ending it here is how a
    // throttled refresh or a 502 on the refresh endpoint became a surprise trip to /login.
    throw new Error('Token refresh unavailable, please retry');
  }

  if (refreshOutcome === 'rejected') {
    // Genuinely no longer authenticated. Dispatch the expiry and let `logoutAction` do the
    // clearing: calling clearAuthentication() first sets the `logoutTransition` flag that
    // logoutAction's own duplicate guard reads, so the logout it triggers bails out and leaves
    // cookies cleared with the store still logged in — a half-logged-out session that never
    // redirects.
    authTool.sendAuthExpired();
    throw new Error('Failed to refresh authentication');
  }

  // Verify the token was actually updated
  const newCookiesData = isServer() ? await getCookieStorageFromServer() : await getClientSessionData();

  if (!newCookiesData?.jwt || newCookiesData.jwt === 'null' || newCookiesData.jwt === 'undefined') {
    gqlDebug('graphql log: errorLink: Token refresh succeeded but no new JWT found');
    // Same ordering rule as above — the logout owns the clearing.
    authTool.sendAuthExpired();
    throw new Error('Token refresh succeeded but no valid JWT available');
  }

  // Force the operation to re-evaluate the authorization header
  // by resetting the context to trigger authLink again
  const currentContext = operation.getContext();
  operation.setContext({
    ...currentContext,
    refreshed: true, // Mark that this is a retry after refresh
    headers: {
      ...currentContext.headers,
      authorization: undefined, // Clear old authorization to force re-evaluation
    },
  });

  // Small delay to ensure session updates are propagated
  await new Promise((resolve) => setTimeout(resolve, 100));

  gqlDebug('graphql log: errorLink: Retrying operation after successful refresh:', operation.operationName);

  // For custom domains, dispatch a recovery event to trigger UI update
  if (!isServer() && typeof window !== 'undefined') {
    const currentHost = window.location.host;

    if (isCustomDomain(currentHost)) {
      gqlDebug(
        'graphql log: errorLink: Custom domain detected, dispatching recovery event for:',
        currentHost,
      );
      // Dispatch event to trigger component re-render
      window.dispatchEvent(
        new CustomEvent('token-refresh-complete', {
          detail: { operationName: operation.operationName },
        }),
      );
    }
  }

  return new Promise((resolve, reject) => {
    forward(operation).subscribe({
      next: resolve,
      error: reject,
    });
  });
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  gqlDebug('graphql log: errorLink : ', operation?.operationName);
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      gqlDebug('graphql log: errorLink error: ', err.extensions?.code, err.message);

      // Skip retry for refresh token operations to prevent loops
      if (operation?.operationName === 'refreshToken') {
        gqlDebug('graphql log: errorLink: Skipping retry for refreshToken operation');
        return;
      }

      // Skip retry for login/signup operations - these are public and don't require auth
      const publicAuthOperations = [
        'signInMagicLink',
        'signInPasswordBased',
        'signUpPasswordBased',
        'signInV2',
        'signUpV2',
        'verifyMagicLink',
        'respondToChallenge',
        'forgotPasswordV2',
        'confirmUserV2',
      ];
      if (publicAuthOperations.includes(operation?.operationName)) {
        gqlDebug(
          'graphql log: errorLink: Skipping retry for public auth operation:',
          operation?.operationName,
        );
        return;
      }

      if (isExpiredJwtGraphQLError(err)) {
        return new Observable<FetchResult<Record<string, any>>>((observer) => {
          refreshAndRetry(operation, forward)
            .then((result) => {
              observer.next(result);
              observer.complete();
            })
            .catch((error) => observer.error(error));
        });
      } else if (err.extensions?.code == 'INTERNAL_SERVER_ERROR' && /status code 50[234]/.test(err.message)) {
        // A 502/503/504 from an upstream service is availability, not authentication — the
        // expired-JWT case is already handled by the branch above, so anything reaching here has
        // been checked and is NOT a token problem. This used to clear auth and expire the session,
        // which turned any gateway blip into a forced logout. Log it and let the error reach the
        // caller; Apollo already delivers it.
        gqlDebug(`graphql log: upstream ${err.message} — surfaced, session left intact`);
      }
    }
  } else {
    gqlDebug('graphql log: errorLink error: No graphql error');
  }

  if (networkError) gqlDebug(`[Network error]: ${networkError}`);
});

const createLink = (GRAPHQL: string | undefined) =>
  createHttpLink({
    // uri: "https://ss-prod.bldr.it/graphql",
    // uri: "https://sswpdev.bldr.it/graphql",
    uri: GRAPHQL,
    fetch: typeof window !== 'undefined' ? window.fetch : fetch,
    credentials: 'include', // Include cookies in GraphQL requests
    // headers: { authorization: localStorage.getItem('jwt') ? `Bearer ${localStorage.getItem('jwt')}` : '' },
  });

export const authLink = setContext(async (operation, previousContext) => {
  // Check if a custom authorization header was already set in the context
  if (previousContext?.headers?.authorization !== undefined) {
    console.log('Using provided authorization header for operation:', operation.operationName);
    return {
      headers: {
        ...previousContext.headers,
      },
    };
  }

  // Skip auth header if context indicates to do so (for refresh token requests)
  if (operation.context?.skipAuthHeader) {
    console.log('Skipping authorization header for operation:', operation.operationName);
    return {
      headers: {
        ...previousContext.headers,
        authorization: '',
      },
    };
  }

  // Always fetch fresh token data - don't cache, especially after refresh
  const cookiesData = isServer() ? await getCookieStorageFromServer() : await getClientSessionData();

  // If this is a retry after refresh, log for debugging
  // In setContext, the context is accessed directly from previousContext
  if (previousContext?.refreshed) {
    console.log('Retry after token refresh for:', operation.operationName);
    console.log('New token present:', !!cookiesData?.jwt);
  }

  // Public auth operations - these should use guest token if expired JWT exists
  const publicAuthOperations = [
    'signInMagicLink',
    'signInPasswordBased',
    'signUpPasswordBased',
    'signInV2',
    'signUpV2',
    'verifyMagicLink',
    'respondToChallenge',
    'forgotPasswordV2',
    'confirmUserV2',
  ];

  const isPublicAuthOp = publicAuthOperations.includes(operation.operationName);

  // Only set authorization header if we have a valid JWT
  const hasValidJwt = cookiesData?.jwt && cookiesData.jwt !== 'null' && cookiesData.jwt !== 'undefined';

  if (isPublicAuthOp) {
    console.log('[authLink] Public auth operation:', operation.operationName);
    console.log('[authLink] Cookie data:', {
      hasJwt: !!cookiesData?.jwt,
      jwtLength: cookiesData?.jwt?.length || 0,
      jwtPreview: cookiesData?.jwt ? cookiesData.jwt.substring(0, 30) + '...' : 'NONE',
      role: cookiesData?.role,
      userId: cookiesData?.userId,
    });
    console.log('[authLink] Will send authorization:', hasValidJwt ? 'YES' : 'NO (EMPTY)');
  } else {
    console.log('[authLink] Regular operation:', operation.operationName, '- hasJwt:', hasValidJwt);
  }

  // For public auth operations, always include the token (even if it's a guest token)
  // The backend requires at least a guest token for all operations
  return {
    headers: {
      ...previousContext.headers,
      authorization: hasValidJwt ? `Bearer ${cookiesData.jwt}` : '',
    },
  };
});

// Track latency metrics per operation to surface slow GraphQL calls
const latencyMetrics: Record<string, number[]> = {};

function calculatePercentile(values: number[], percentile: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.ceil((percentile / 100) * sorted.length) - 1;
  return sorted[index];
}

function recordLatency(operationName: string, latency: number): number {
  const metrics = latencyMetrics[operationName] || [];
  metrics.push(latency);
  // Keep only the latest 100 measurements to bound memory usage
  if (metrics.length > 100) {
    metrics.shift();
  }
  latencyMetrics[operationName] = metrics;
  return calculatePercentile(metrics, 95);
}

// Apollo link to emit structured metrics for each GraphQL request
const metricsLink = new ApolloLink((operation, forward) => {
  const start = Date.now();
  return new Observable((observer) => {
    forward(operation).subscribe({
      next: (result) => {
        const latency = Date.now() - start;
        const status = (result as any)?.extensions?.http?.status ?? 200;
        const operationName = operation.operationName || 'anonymous';
        const p95 = recordLatency(operationName, latency);
        serverLogger.log('GraphQL request', {
          operationName,
          status,
          latency_ms: latency,
          p95_latency_ms: p95,
        });
        observer.next(result);
      },
      error: (error) => {
        const latency = Date.now() - start;
        const status = error?.networkError?.statusCode ?? error?.statusCode ?? 500;
        const operationName = operation.operationName || 'anonymous';
        const p95 = recordLatency(operationName, latency);
        serverLogger.error('GraphQL request failed', {
          operationName,
          status,
          latency_ms: latency,
          p95_latency_ms: p95,
        });
        observer.error(error);
      },
      complete: observer.complete.bind(observer),
    });
  });
});

export function getApolloClient(
  GRAPHQL: string | undefined = gqlPath,
  token = '',
): ApolloClient<NormalizedCacheObject> {
  // if(token) {
  //   const serverAuthLink = setContext(async (operation, { headers }) => {

  //     return {
  //       headers: {
  //         ...headers,
  //         authorization: token ? `Bearer ${token}` : '',
  //       },
  //     };
  //   });

  //   return new ApolloClient({
  //     ssrMode: false,
  //     link: ApolloLink.from([errorLink, serverAuthLink, createLink(GRAPHQL)]),
  //     cache: new InMemoryCache(),
  //   });
  // } else {
  return new ApolloClient({
    ssrMode: false,
    link: ApolloLink.from([errorLink, authLink, metricsLink, createLink(GRAPHQL)]),
    cache: new InMemoryCache(),
  });
  // }
}

export function initializeGQLClient(
  GRAPHQL: string | undefined,
  authToolInst = { sendAuthExpired: () => {}, refreshContextData: () => {}, clearCartData: () => {} },
) {
  gqlClient = getApolloClient(GRAPHQL);
  authTool = authToolInst;
  gqlPath = GRAPHQL;
}

export async function getNewJwt(tenantId: string, refreshToken: string, expiredJwt?: string) {
  // Validate inputs to prevent null/undefined values
  if (!tenantId || tenantId === 'null' || tenantId === 'undefined') {
    console.error('getNewJwt: Invalid tenantId provided:', tenantId);
    return { data: null, error: 'Invalid tenantId' };
  }

  if (!refreshToken || refreshToken === 'null' || refreshToken === 'undefined') {
    console.error('getNewJwt: Invalid refreshToken provided:', refreshToken);
    return { data: null, error: 'Invalid refreshToken' };
  }

  const gqlClient = getApolloClient(appSettings?.api.GRAPHQL);
  const query = gql`
  query refreshToken {
   getJwtTokenV2(tenantId: "${tenantId}", refreshToken: "${refreshToken}") {
       jwt
       refreshToken  
     }
    }
  `;
  if (gqlClient !== undefined) {
    // Get userId for cache key scoping
    let userId: string | undefined;
    try {
      const cookiesData = isServer() ? await getCookieStorageFromServer() : await getClientSessionData();
      userId = cookiesData?.userId;
    } catch {
      userId = undefined;
    }

    const refreshKey = getRefreshPromiseKey(tenantId, refreshToken, userId);
    if (!refreshTokenPromises.has(refreshKey)) {
      const refreshPromise = (async () => {
        try {
          // Use passed expiredJwt if provided, otherwise try to get from cookies
          let jwtToUse = expiredJwt;
          if (!jwtToUse) {
            const cookiesData = isServer()
              ? await getCookieStorageFromServer()
              : await getClientSessionData();
            jwtToUse = cookiesData?.jwt;
            // console.log(
            //   'getNewJwt: Retrieved JWT from cookies:',
            //   !!jwtToUse,
            //   'First 20 chars:',
            //   jwtToUse?.substring(0, 20),
            // );
          } else {
            // console.log('getNewJwt: Using passed expired JWT, First 20 chars:', jwtToUse?.substring(0, 20));
          }

          // Create context with the expired JWT in the authorization header
          const refreshContext: any = {};

          if (jwtToUse && jwtToUse !== 'null' && jwtToUse !== 'undefined') {
            refreshContext.headers = {
              authorization: `Bearer ${jwtToUse}`,
            };
            gqlDebug('getNewJwt: Using expired JWT in authorization header for refresh token call');
          } else {
            // If no JWT is available, skip the auth header
            refreshContext.skipAuthHeader = true;
            gqlDebug('getNewJwt: No expired JWT available, skipping authorization header');
          }

          // Log minimal useful information
          console.log('getNewJwt: Refreshing token with', {
            hasExpiredJwt: !!refreshContext.headers?.authorization,
            tenantId: tenantId,
          });
          // Detailed logging commented out for production
          // console.log('=== REFRESH TOKEN REQUEST DETAILS ===');
          // console.log('Timestamp:', new Date().toISOString());
          // console.log('Function: getNewJwt');
          // console.log('Endpoint:', appSettings?.api.GRAPHQL);
          // console.log('Request Payload:', {
          //   tenantId: tenantId,
          //   refreshToken: refreshToken.substring(0, 10) + '...', // Log only first 10 chars for security
          // });
          // console.log('Request Headers:', {
          //   authorization: refreshContext.headers?.authorization
          //     ? `Bearer ${refreshContext.headers.authorization.substring(7, 20)}...` // Log partial token
          //     : 'No authorization header',
          //   skipAuthHeader: refreshContext.skipAuthHeader || false,
          // });
          // console.log('GraphQL Query:', query.loc?.source.body);
          // console.log('=====================================')

          const result = await gqlClient
            .query({
              query: query,
              context: refreshContext,
            })
            .then((result) => {
              // console.log('=== REFRESH TOKEN RESPONSE ===');
              // console.log('Success: true');
              // console.log('New JWT received:', !!result?.data?.getJwtTokenV2?.jwt);
              // console.log('New RefreshToken received:', !!result?.data?.getJwtTokenV2?.refreshToken);
              // console.log('==============================');
              return result;
            })
            .catch((error) => {
              console.error('getNewJwt: Refresh failed -', error.message);
              // console.error('=== REFRESH TOKEN ERROR ===');
              // console.error('Error:', error.message);
              // console.error('GraphQL Errors:', error.graphQLErrors);
              // console.error('Network Error:', error.networkError);
              // console.error('===========================');
              throw error;
            });

          return result;
        } catch (error) {
          console.error('getNewJwt: Exception -', error);
          // console.error('=== REFRESH TOKEN EXCEPTION ===');
          // console.error('Exception in getNewJwt:', error);
          // console.error('===============================');
          throw error;
        } finally {
          refreshTokenPromises.delete(refreshKey);
        }
      })();
      refreshTokenPromises.set(refreshKey, refreshPromise);
    }
    return refreshTokenPromises.get(refreshKey) as Promise<FetchResult<Record<string, any>>>;
  }

  return new Promise((resolve, reject) => {
    reject();
  });
}

// Version with custom domain support via headers
export async function getNewJwtWithHeaders(
  tenantId: string,
  refreshToken: string,
  hostName: string,
  expiredJwt?: string,
) {
  // Validate inputs to prevent null/undefined values
  if (!tenantId || tenantId === 'null' || tenantId === 'undefined') {
    console.error('getNewJwtWithHeaders: Invalid tenantId provided:', tenantId);
    return { data: null, error: 'Invalid tenantId' };
  }

  if (!refreshToken || refreshToken === 'null' || refreshToken === 'undefined') {
    console.error('getNewJwtWithHeaders: Invalid refreshToken provided:', refreshToken);
    return { data: null, error: 'Invalid refreshToken' };
  }

  const gqlClient = getApolloClient(appSettings?.api.GRAPHQL);
  const query = gql`
  query refreshToken {
   getJwtTokenV2(tenantId: "${tenantId}", refreshToken: "${refreshToken}") {
       jwt
       refreshToken  
     }
    }
  `;
  if (gqlClient !== undefined) {
    try {
      // Use passed expiredJwt if provided, otherwise try to get from cookies
      let jwtToUse = expiredJwt;
      if (!jwtToUse) {
        const cookiesData = isServer() ? await getCookieStorageFromServer() : await getClientSessionData();
        jwtToUse = cookiesData?.jwt;
        // console.log(
        //   'getNewJwtWithHeaders: Retrieved JWT from cookies:',
        //   !!jwtToUse,
        //   'First 20 chars:',
        //   jwtToUse?.substring(0, 20),
        // );
      } else {
        // console.log(
        //   'getNewJwtWithHeaders: Using passed expired JWT, First 20 chars:',
        //   jwtToUse?.substring(0, 20),
        // );
      }

      // Create headers with custom domain support
      const refreshHeaders: any = {
        Origin: hostName,
        Referer: hostName,
      };

      const refreshContext: any = {
        headers: refreshHeaders,
      };

      // Add expired JWT as authorization if available
      if (jwtToUse && jwtToUse !== 'null' && jwtToUse !== 'undefined') {
        refreshHeaders.authorization = `Bearer ${jwtToUse}`;
        gqlDebug('getNewJwtWithHeaders: Using expired JWT in authorization header for refresh token call');
      } else {
        // If no JWT is available, skip the auth header
        refreshContext.skipAuthHeader = true;
        gqlDebug('getNewJwtWithHeaders: No expired JWT available, skipping authorization header');
      }

      // Log minimal useful information
      console.log('getNewJwtWithHeaders: Refreshing token with', {
        hasExpiredJwt: !!refreshHeaders.authorization,
        tenantId: tenantId,
        hostName: hostName,
      });
      // Detailed logging commented out for production
      // console.log('=== REFRESH TOKEN REQUEST DETAILS (WITH HEADERS) ===');
      // console.log('Timestamp:', new Date().toISOString());
      // console.log('Function: getNewJwtWithHeaders');
      // console.log('Endpoint:', appSettings?.api.GRAPHQL);
      // console.log('HostName:', hostName);
      // console.log('Request Payload:', {
      //   tenantId: tenantId,
      //   refreshToken: refreshToken.substring(0, 10) + '...', // Log only first 10 chars for security
      // });
      // console.log('Request Headers:', {
      //   authorization: refreshHeaders.authorization
      //     ? `Bearer ${refreshHeaders.authorization.substring(7, 20)}...` // Log partial token
      //     : 'No authorization header',
      //   Origin: refreshHeaders.Origin,
      //   Referer: refreshHeaders.Referer,
      //   skipAuthHeader: refreshContext.skipAuthHeader || false,
      // });
      // console.log('GraphQL Query:', query.loc?.source.body);
      // console.log('====================================================')

      return gqlClient
        .query({
          query: query,
          context: refreshContext,
        })
        .then((result) => {
          // console.log('=== REFRESH TOKEN RESPONSE (WITH HEADERS) ===');
          // console.log('Success: true');
          // console.log('New JWT received:', !!result?.data?.getJwtTokenV2?.jwt);
          // console.log('New RefreshToken received:', !!result?.data?.getJwtTokenV2?.refreshToken);
          // console.log('==============================================');
          return result;
        })
        .catch((error) => {
          console.error('getNewJwtWithHeaders: Refresh failed -', error.message);
          // console.error('=== REFRESH TOKEN ERROR (WITH HEADERS) ===');
          // console.error('Error:', error.message);
          // console.error('GraphQL Errors:', error.graphQLErrors);
          // console.error('Network Error:', error.networkError);
          // console.error('==========================================');
          throw error;
        });
    } catch (error) {
      console.error('getNewJwtWithHeaders: Exception -', error);
      // console.error('=== REFRESH TOKEN EXCEPTION (WITH HEADERS) ===');
      // console.error('Exception in getNewJwtWithHeaders:', error);
      // console.error('===============================================');
      return Promise.reject(error);
    }
  }

  return new Promise((resolve, reject) => {
    reject();
  });
}

export async function getTenantInfoJwt(tenantId?: string) {
  const gqlClient = getApolloClient(appSettings?.api.GRAPHQL);
  const query = gql`
    query {
      getTenantInfo {
        jwt
        refreshToken
      }
    }
  `;

  // Resolve tenant ID and userId if not provided
  let userId: string | undefined;
  if (!tenantId) {
    try {
      const cookiesData = isServer() ? await getCookieStorageFromServer() : await getClientSessionData();
      tenantId = cookiesData?.tenantId || '';
      userId = cookiesData?.userId;
    } catch {
      tenantId = '';
      userId = undefined;
    }
  } else {
    // Still get userId for cache scoping even if tenantId is provided
    try {
      const cookiesData = isServer() ? await getCookieStorageFromServer() : await getClientSessionData();
      userId = cookiesData?.userId;
    } catch {
      userId = undefined;
    }
  }

  // SECURITY FIX: Include userId in cache key to prevent cross-user leakage
  const cacheKey = userId ? `${tenantId || 'default'}:${userId}` : tenantId || 'default';
  const now = Date.now();
  const cached = tenantInfoCache.get(cacheKey);
  if (cached && now - cached.timestamp < TENANT_INFO_CACHE_DURATION) {
    // Double-check userId matches to prevent cache poisoning
    if (cached.userId === userId) {
      return cached.promise;
    } else {
      // User mismatch, clear invalid cache
      tenantInfoCache.delete(cacheKey);
    }
  }

  if (gqlClient !== undefined) {
    // Modify headers to include referer
    const options = {
      query: query,
      context: {
        headers: {
          referer: typeof window !== 'undefined' ? window.location.origin : appSettings?.api?.CLIENT_APP_URL,
          origin: typeof window !== 'undefined' ? window.location.origin : appSettings?.api?.CLIENT_APP_URL,
          // Add any other headers if needed
        },
      },
    };

    const promise = gqlClient
      .query(options)
      .then((result) => {
        if (!result?.data?.getTenantInfo) {
          console.error('getTenantInfo returned null or undefined:', result);
          tenantInfoCache.delete(cacheKey);
          throw new Error('getTenantInfo query returned null for non-nullable field');
        }
        return result;
      })
      .catch((error) => {
        console.error('Error in getTenantInfoJwt:', error);
        tenantInfoCache.delete(cacheKey);
        throw error;
      });

    tenantInfoCache.set(cacheKey, { promise, timestamp: now, userId });
    return promise;
  }

  return new Promise((_, reject) => {
    reject(new Error('GraphQL client is undefined'));
  });
}

///ssr
export function getSSRApolloClient(
  GRAPHQL: string | undefined = gqlPath,
  // token = localStorage.getItem('jwt'),
): ApolloClient<any> {
  return new ApolloClient({
    uri: GRAPHQL,
    cache: new InMemoryCache(),
    // headers: { Origin: 'https://spaces.dev.bldr.it', Referer: 'https://spaces.dev.bldr.it' }
  });
}

export async function getTenantInfoJwtSSR(hostName: string, cookieHeader?: string, correlationId?: string) {
  const key = `${hostName}|${cookieHeader || ''}|${correlationId || ''}`;
  const existing = tenantInfoSSRInflight.get(key);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    const start = Date.now();
    const gqlClient = getSSRApolloClient(appSettings?.api.GRAPHQL);
    const query = gql`
      query {
        getTenantInfo {
          jwt
          refreshToken
        }
      }
    `;

    if (gqlClient !== undefined) {
      try {
        const headers: any = {
          Origin: `${hostName}`,
          Referer: `${hostName}`,
        };

        // Include cookies if provided (for SSR calls from API routes)
        if (cookieHeader) {
          headers.Cookie = cookieHeader;
        }

        if (correlationId) {
          headers['x-correlation-id'] = correlationId;
        }

        const result = await gqlClient.query({
          query: query,
          context: {
            headers,
          },
          errorPolicy: 'all', // Return both data and errors
        });

        const latency = Date.now() - start;
        const status = (result as any)?.extensions?.http?.status ?? 200;
        const p95 = recordLatency('getTenantInfo', latency);
        serverLogger.debug('GraphQL request', {
          endpoint: appSettings?.api.GRAPHQL,
          operationName: 'getTenantInfo',
          correlationId,
          status,
          latency_ms: latency,
          p95_latency_ms: p95,
        });

        // Check if the result is valid
        if (!result?.data?.getTenantInfo) {
          // Return a structured error response instead of throwing
          // No need to log this as an error since it's handled gracefully
          return {
            data: null,
            error: new Error('getTenantInfo query returned null for non-nullable field'),
          };
        }

        return result;
      } catch (error: any) {
        const latency = Date.now() - start;
        const status = error?.networkError?.statusCode ?? error?.statusCode ?? 500;
        const p95 = recordLatency('getTenantInfo', latency);
        serverLogger.debug('GraphQL request failed', {
          endpoint: appSettings?.api.GRAPHQL,
          operationName: 'getTenantInfo',
          correlationId,
          status,
          latency_ms: latency,
          p95_latency_ms: p95,
        });

        console.error('Error in getTenantInfoJwtSSR:', error);
        throw error;
      }
    }

    return new Promise((resolve, reject) => {
      reject(new Error('GraphQL client is undefined'));
    });
  })();

  tenantInfoSSRInflight.set(key, promise);

  return promise.finally(() => {
    tenantInfoSSRInflight.delete(key);
  });
}
